import { ElementType } from "react";
import { PlusIcon } from "lucide-react";

import { Center } from "@/components/Center";
import { Column } from "@/components/Column";
import { ErrorText } from "@/components/ErrorText";
import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export const EmptyState = ({
  className = "py-4",
  description,
  descriptionClassName,
  handlePrimaryClick,
  headerClassName = "text-2xl",
  Icon,
  iconContainerClassName,
  IsError,
  isErrorMessage,
  isIconHidden = false,
  IsLoading,
  PrimaryButtonIcon,
  primaryButtonClassName,
  primaryButtonName,
  primaryButtonVariant,
  SecondaryButtonComponent,
  title,
  warningMessage,
}: {
  className?: string;
  description: string;
  descriptionClassName?: string;
  handlePrimaryClick?: () => void;
  headerClassName?: string;
  Icon: ElementType;
  iconContainerClassName?: string;
  IsError?: boolean;
  isErrorMessage?: string;
  isIconHidden?: boolean;
  IsLoading?: boolean;
  PrimaryButtonIcon?: ElementType;
  primaryButtonClassName?: string;
  primaryButtonName?: string;
  primaryButtonVariant?: "default" | "outline" | "secondary";
  SecondaryButtonComponent?: ElementType;
  title: string;
  warningMessage?: string;
}) => {
  const render = () => (
    <Column
      className={cn("w-full items-center justify-center gap-6", className)}
    >
      {isIconHidden ? null : (
        <Center
          className={cn(
            "rounded-full bg-slate-100 p-4",
            iconContainerClassName,
          )}
        >
          <Icon className="h-8 w-8 text-slate-600" />
        </Center>
      )}
      <Column className="items-center gap-4">
        <Column className="items-center justify-center gap-2">
          <Label
            className={cn(
              "text-slate font-semibold text-slate-600",
              headerClassName,
            )}
          >
            {title}
          </Label>
          <Label
            className={cn("font-normal text-slate-500", descriptionClassName)}
          >
            {description}
          </Label>
          {warningMessage && (
            <p className="text-sm text-red-500">{warningMessage}</p>
          )}
        </Column>
        <Row className="flex items-center gap-3">
          {handlePrimaryClick && (
            <Button
              className={cn("rounded-full", primaryButtonClassName)}
              onClick={handlePrimaryClick}
              type="button"
              variant={primaryButtonVariant}
            >
              {IsLoading ? (
                <LoadingIndicator gearColor="white" variant="small" />
              ) : PrimaryButtonIcon ? (
                <PrimaryButtonIcon className="h-4 w-4" />
              ) : (
                <PlusIcon className="h-4 w-4" />
              )}
              {primaryButtonName}
            </Button>
          )}
          {SecondaryButtonComponent && (
            <SecondaryButtonComponent buttonVariant="outline" />
          )}
        </Row>
        {IsError && (
          <ErrorText
            errorMessage={isErrorMessage ?? "An unknown error occurred."}
          />
        )}
      </Column>
    </Column>
  );

  return render();
};
